import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { consentAPI } from '../lib/api';
import type { ConsentStatus, RecordConsentBody } from '../types/consent';

export function useLeadConsent(leadId: string | undefined) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  return useQuery<ConsentStatus>({
    queryKey: ['lead-consent', orgId, leadId],
    queryFn: async () => {
      const { data } = await consentAPI.getStatus(leadId!, orgId!);
      return data;
    },
    enabled: !!leadId && !!orgId,
    staleTime: 0,
  });
}

export function useRecordConsent() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<{ recorded: true }, Error, RecordConsentBody>({
    mutationFn: async (body) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await consentAPI.record(orgId, body);
      return data;
    },
    onSuccess: (_data, variables) => {
      const orgId = currentOrganization?.id;
      queryClient.invalidateQueries({ queryKey: ['lead-consent', orgId, variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
    },
  });
}
